import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Descriptions, Tag, Spin, Row, Col, Image, Switch } from 'antd';
import { EditOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/common/PageHeader';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import LazyImage from '../../components/common/LazyImage';
import { useErrorHandling } from '../../hooks/useErrorHandling';
import fixtureService from '../../services/fixtureService';
import { FixtureType } from '../../types/fixture';

const FixtureDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { error, setError, clearError } = useErrorHandling();

  const [fixture, setFixture] = useState<FixtureType | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) {
      loadFixtureDetail(id);
    }
  }, [id]);

  const loadFixtureDetail = async (fixtureId: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await fixtureService.getFixtureById(fixtureId);
      
      if (response.success && response.data) {
        setFixture(response.data);
      } else {
        setError(new Error(response.errorMessage || t('fixture.getFixtureDetailFailed')));
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (fixture) {
      navigate(`/fixture/edit/${fixture.id}`);
    }
  };

  const handleBack = () => {
    navigate('/fixture/list');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'green';
      case 'Draft':
        return 'orange';
      case 'Inactive':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Active':
        return t('fixture.statusText.Active');
      case 'Draft':
        return t('fixture.statusText.Draft');
      case 'Inactive':
        return t('fixture.statusText.Inactive');
      default:
        return status;
    }
  };

  const formatDimensions = (dimensions: any) => {
    if (!dimensions) return '-';
    return `${dimensions.width} × ${dimensions.height} × ${dimensions.depth} ${dimensions.unit}`;
  };

  const formatHoleCount = (count: number) => {
    if (count === undefined || count === null) return '-';
    return `${count} ${t('fixture.holeCount')}`;
  };

  if (error) {
    return (
      <div>
        <PageHeader
          title={t('fixture.detailTitle')}
          backPath="/fixture/list"
        />
        <ErrorDisplay
          error={error}
          onRetry={() => id && loadFixtureDetail(id)}
        />
      </div>
    );
  }

  if (loading || !fixture) {
    return (
      <div>
        <PageHeader
          title={t('fixture.detailTitle')}
          backPath="/fixture/list"
        />
        <Card>
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('fixture.detailTitle')}
        backPath="/fixture/list"
        extra={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
            >
              {t('common.back')}
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEdit}
            >
              {t('common.edit')}
            </Button>
          </Space>
        }
      />

      <Row gutter={24}>
        <Col span={16}>
          <Card title={t('common.basicInfo')}>
            <Descriptions column={2} bordered>
              <Descriptions.Item label={t('fixture.typeName')}>
                {fixture.typeName}
              </Descriptions.Item>
              <Descriptions.Item label={t('fixture.typeCode')}>
                {fixture.typeCode}
              </Descriptions.Item>
              <Descriptions.Item label={t('fixture.status')}>
                <Tag color={getStatusColor(fixture.status)}>
                  {getStatusText(fixture.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('fixture.dimensions')}>
                {formatDimensions(fixture.dimensions)}
              </Descriptions.Item>
              <Descriptions.Item label={t('fixture.holeCount')}>
                {formatHoleCount(fixture.holeCount)}
              </Descriptions.Item>
              <Descriptions.Item label={t('fixture.supportHold')}>
                <Switch 
                  checked={fixture.supportHold} 
                  checkedChildren={t('common.yes')} 
                  unCheckedChildren={t('common.no')} 
                  disabled 
                />
              </Descriptions.Item>
              <Descriptions.Item label={t('common.createTime')}>
                {new Date(fixture.gmtCreate).toLocaleString('zh-CN')}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.modifyTime')}>
                {new Date(fixture.gmtModified).toLocaleString('zh-CN')}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.creator')}>
                {fixture.creatorName}
              </Descriptions.Item>
              <Descriptions.Item label={t('common.modifier')}>
                {fixture.modifierName}
              </Descriptions.Item>
              {fixture.description && (
                <Descriptions.Item label={t('fixture.description')} span={2}>
                  {fixture.description}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>
        
        <Col span={8}>
          <Card title={t('fixture.image')} className="fixture-detail-card">
            <div className="fixture-image-container">
              <Image
                src={fixture.imageUrl}
                alt={``}
                width={300}
                height={225}
                className="fixture-image"
                style={{ 
                  width: '100%',
                  borderRadius: 6,
                  border: '1px solid #d9d9d9'
                }}
              />
            </div>
            
            {fixture.imageUrl && (
              <div style={{ 
                marginTop: 12, 
                fontSize: 12, 
                color: '#666',
                textAlign: 'center'
              }}>
                {t('common.clickToViewLarge')}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FixtureDetail;